import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Save, AlertTriangle, Mail, Server } from 'lucide-react';

export function AdminSettingsTab({
  announcement, setAnnouncement, onSaveAnnouncement, savingAnnouncement,
  cancellationEmail, setCancellationEmail,
  telegramBotToken, setTelegramBotToken, telegramChatId, setTelegramChatId,
  smtpHost, setSmtpHost, smtpPort, setSmtpPort, smtpEmail, setSmtpEmail, smtpPassword, setSmtpPassword,
  onSaveSettings, savingSettings
}) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2"><AlertTriangle className="w-6 h-6 text-red-500" /> Gestione Avviso Importante</CardTitle>
          <p className="text-sm text-muted-foreground">Messaggio visibile a tutti gli utenti nella pagina principale</p>
        </CardHeader>
        <CardContent className="space-y-6">
          {announcement.is_active && announcement.message && (
            <div className="p-4 rounded-lg border-2 border-red-500 bg-red-50 dark:bg-red-950/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-800 dark:text-red-200 font-medium text-sm sm:text-base">{announcement.message}</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div className="space-y-1">
              <Label htmlFor="announcement-active" className="font-semibold">Attiva Avviso</Label>
              <p className="text-sm text-muted-foreground">Quando attivo, visibile a tutti</p>
            </div>
            <Switch id="announcement-active" checked={announcement.is_active} onCheckedChange={(checked) => setAnnouncement({ ...announcement, is_active: checked })} data-testid="announcement-active-switch" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="announcement-message" className="font-semibold">Messaggio Avviso</Label>
            <Textarea id="announcement-message" placeholder="Es: Prenotazioni sospese dal 24/12 al 6/1..." value={announcement.message} onChange={(e) => setAnnouncement({ ...announcement, message: e.target.value })} rows={4} className="resize-none" data-testid="announcement-message-input" />
          </div>
          <Button onClick={onSaveAnnouncement} disabled={savingAnnouncement} className="btn-scale" data-testid="save-announcement-button">
            <Save className="w-4 h-4 mr-2" />{savingAnnouncement ? 'Salvataggio...' : 'Salva Avviso'}
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2"><Mail className="w-6 h-6" /> Email per Richieste di Cancellazione</CardTitle>
          <p className="text-sm text-muted-foreground">Gli utenti potranno inviare una richiesta di cancellazione a questo indirizzo email</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cancellation-email" className="font-semibold">Indirizzo Email</Label>
            <Input id="cancellation-email" type="email" placeholder="admin@comune-laval.it" value={cancellationEmail} onChange={(e) => setCancellationEmail(e.target.value)} data-testid="cancellation-email-input" />
          </div>
          <Button onClick={onSaveSettings} disabled={savingSettings} className="btn-scale" data-testid="save-settings-button">
            <Save className="w-4 h-4 mr-2" />{savingSettings ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
          <div className="p-4 rounded-lg bg-muted">
            <p className="text-sm text-muted-foreground"><strong>Come funziona:</strong> Ogni prenotazione ha un bottone &quot;Richiedi Cancellazione&quot;. Quando l&apos;utente lo preme, si apre l&apos;app email con i dettagli della prenotazione inviati a questo indirizzo.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="font-heading text-2xl">Notifiche Telegram</CardTitle>
          <p className="text-sm text-muted-foreground">Ricevi notifiche automatiche su Telegram ad ogni nuova prenotazione</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="telegram-token" className="font-semibold">Bot Token</Label>
            <Input id="telegram-token" type="text" placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11" value={telegramBotToken} onChange={(e) => setTelegramBotToken(e.target.value)} data-testid="telegram-token-input" />
            <p className="text-xs text-muted-foreground">Crea un bot su Telegram tramite @BotFather e copia il token qui</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="telegram-chat" className="font-semibold">Chat ID</Label>
            <Input id="telegram-chat" type="text" placeholder="-1001234567890" value={telegramChatId} onChange={(e) => setTelegramChatId(e.target.value)} data-testid="telegram-chat-input" />
            <p className="text-xs text-muted-foreground">ID del gruppo o della chat dove inviare le notifiche.</p>
          </div>
          <Button onClick={onSaveSettings} disabled={savingSettings} className="btn-scale" data-testid="save-telegram-button">
            <Save className="w-4 h-4 mr-2" />{savingSettings ? 'Salvataggio...' : 'Salva Impostazioni Telegram'}
          </Button>
        </CardContent>
      </Card>

      {/* SMTP Email Settings */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2"><Server className="w-6 h-6" /> Configurazione Email (SMTP)</CardTitle>
          <p className="text-sm text-muted-foreground">Configura il server SMTP per inviare email di conferma prenotazione con ricevuta PDF allegata</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp-host" className="font-semibold">Server SMTP</Label>
              <Input id="smtp-host" type="text" placeholder="smtp.gmail.com" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} data-testid="smtp-host-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-port" className="font-semibold">Porta</Label>
              <Input id="smtp-port" type="number" placeholder="587" value={smtpPort} onChange={(e) => setSmtpPort(parseInt(e.target.value) || 587)} data-testid="smtp-port-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-email" className="font-semibold">Email Mittente</Label>
              <Input id="smtp-email" type="email" placeholder="noreply@comune-laval.it" value={smtpEmail} onChange={(e) => setSmtpEmail(e.target.value)} data-testid="smtp-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp-password" className="font-semibold">Password / App Password</Label>
              <Input id="smtp-password" type="password" placeholder="La password del server email" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} data-testid="smtp-password-input" />
            </div>
          </div>
          <Button onClick={onSaveSettings} disabled={savingSettings} className="btn-scale" data-testid="save-smtp-button">
            <Save className="w-4 h-4 mr-2" />{savingSettings ? 'Salvataggio...' : 'Salva Configurazione Email'}
          </Button>
          <div className="p-4 rounded-lg bg-muted">
            <p className="text-sm text-muted-foreground">
              <strong>Come funziona:</strong> Ad ogni nuova prenotazione, il sistema invia automaticamente un&apos;email di conferma all&apos;utente con la ricevuta PDF allegata. Per Gmail usa &quot;App Password&quot; (Impostazioni Google &gt; Sicurezza &gt; Password per le app).
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
